import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Lock, Calendar, Info } from 'lucide-react';
import AuthContext from '../contexts/AuthContext';

const AccountStatusPage = () => {
  const [statusDetails, setStatusDetails] = useState(null);
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  
  useEffect(() => {
    // Get status details from localStorage
    const statusDetailsStr = localStorage.getItem('statusDetails');
    
    if (statusDetailsStr) {
      try {
        const details = JSON.parse(statusDetailsStr);
        setStatusDetails(details);
      } catch (error) {
        console.error('Error parsing status details:', error);
      }
    } else {
      // If no status details, redirect to login
      navigate('/login');
    }
  }, [navigate]);
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    return new Date(parseInt(timestamp)).toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  if (!statusDetails) {
    return <div className="p-8 text-center">Loading...</div>;
  }
  
  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <AlertTriangle 
            className={`mx-auto h-16 w-16 ${
              statusDetails.status === 'suspended' 
                ? 'text-yellow-500' 
                : 'text-red-500'
            }`} 
          />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            {statusDetails.status === 'suspended' ? 'Account Suspended' : 'Account Banned'}
          </h2>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-6">
            <div className={`p-4 rounded-md ${
              statusDetails.status === 'suspended' 
                ? 'bg-yellow-50 dark:bg-yellow-900/20' 
                : 'bg-red-50 dark:bg-red-900/20'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {statusDetails.status === 'suspended' ? (
                    <Calendar className="h-5 w-5 text-yellow-400" />
                  ) : (
                    <Lock className="h-5 w-5 text-red-400" />
                  )}
                </div>
                <div className="ml-3">
                  <h3 className={`text-sm font-medium ${
                    statusDetails.status === 'suspended' 
                      ? 'text-yellow-800 dark:text-yellow-200' 
                      : 'text-red-800 dark:text-red-200'
                  }`}>
                    {statusDetails.status === 'suspended' 
                      ? 'Your account has been temporarily suspended' 
                      : 'Your account has been permanently banned'
                    }
                  </h3>
                  <div className={`mt-2 text-sm ${
                    statusDetails.status === 'suspended' 
                      ? 'text-yellow-700 dark:text-yellow-300' 
                      : 'text-red-700 dark:text-red-300'
                  }`}>
                    <p>
                      {statusDetails.reason || 'Violation of platform terms of service'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {statusDetails.status === 'suspended' && statusDetails.until && (
              <div className="rounded-md bg-gray-50 dark:bg-gray-700 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-300" />
                  </div>
                  <div className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    <p className="font-medium">Suspension will be lifted on:</p>
                    <p className="mt-1">{formatDate(statusDetails.until)}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Info className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Need assistance?
                  </h3>
                  <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                    <p>
                      If you believe this action was taken in error, please contact our support team.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <button
                onClick={handleLogout}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Return to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountStatusPage;